import React, { useState, useEffect } from 'react';
import {
  Box, Button, Grid, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Alert, Pagination,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
} from '@mui/material';
import {
  Add, Edit, Delete, Build, Refresh,
  HourglassEmpty, Event, Autorenew, CheckCircle,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import frotaService from '../../services/frotaService';
import { ManutencaoVeiculo } from '../../types/frota';
import VeiculoSelect from '../../components/selects/VeiculoSelect';
import FuncionariosSelect from '../../components/selects/FuncionariosSelect';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import FrotaPageLayout, { FrotaStatItem } from '../../components/frota/FrotaPageLayout';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { formatarValorOnInput, parsearValorFormatado, formatarMoedaParaInput } from '../../utils/formatToView';

const STATUS_COLORS = {
  pendente: '#FF9800',
  agendada: '#2196F3',
  em_andamento: '#9C27B0',
  concluida: '#4CAF50',
} as const;

const dateToYmd = (date: Date | null) => {
  if (!date) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const ManutencoesPage: React.FC = () => {
  const [items, setItems] = useState<ManutencaoVeiculo[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [dataRange, setDataRange] = useState<{ startDate: Date | null; endDate: Date | null }>({ startDate: null, endDate: null });
  const [filtroVeiculoId, setFiltroVeiculoId] = useState<number | null>(null);
  const [filtroTipo, setFiltroTipo] = useState<ManutencaoVeiculo['tipo'] | ''>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ total: 0, pendente: 0, agendada: 0, em_andamento: 0, concluida: 0 });
  const [openForm, setOpenForm] = useState(false);
  const [selected, setSelected] = useState<ManutencaoVeiculo | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [form, setForm] = useState({
    veiculo_id: null as number | null, tipo: 'corretiva' as ManutencaoVeiculo['tipo'],
    problema: '', descricao_servico: '', mecanica: '', valor: 0, kilometragem: 0,
    data_manutencao: new Date().toISOString().split('T')[0], status: 'concluida' as ManutencaoVeiculo['status'],
    funcionario_solicitante_id: null as number | null, observacoes: '', escritorio_id: null as number | null,
  });
  const [valorFormatado, setValorFormatado] = useState('0,00');

  const loadStats = async () => {
    try {
      const [totalRes, pendenteRes, agendadaRes, emAndamentoRes, concluidaRes] = await Promise.all([
        frotaService.listarManutencoes({ limit: 1 }),
        frotaService.listarManutencoes({ status: 'pendente', limit: 1 }),
        frotaService.listarManutencoes({ status: 'agendada', limit: 1 }),
        frotaService.listarManutencoes({ status: 'em_andamento', limit: 1 }),
        frotaService.listarManutencoes({ status: 'concluida', limit: 1 }),
      ]);
      setStats({
        total: totalRes.pagination?.total || 0,
        pendente: pendenteRes.pagination?.total || 0,
        agendada: agendadaRes.pagination?.total || 0,
        em_andamento: emAndamentoRes.pagination?.total || 0,
        concluida: concluidaRes.pagination?.total || 0,
      });
    } catch {
      /* mantém stats anteriores */
    }
  };

  const buildFiltros = () => ({
    status: status || undefined,
    data_inicio: dateToYmd(dataRange.startDate) || undefined,
    data_fim: dateToYmd(dataRange.endDate) || undefined,
    veiculo_id: filtroVeiculoId ?? undefined,
    tipo: filtroTipo || undefined,
    page,
    limit: 20,
  });

  const load = async () => {
    try {
      setLoading(true);
      const res = await frotaService.listarManutencoes(buildFiltros());
      setItems(res.data || []);
      setTotalPages(res.pagination?.pages || 1);
    } catch {
      toast.error('Erro ao carregar manutenções');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [status, dataRange, filtroVeiculoId, filtroTipo, page]);

  const resetForm = () => {
    setValorFormatado('0,00');
    setForm({
      veiculo_id: null, tipo: 'corretiva', problema: '', descricao_servico: '', mecanica: '',
      valor: 0, kilometragem: 0, data_manutencao: new Date().toISOString().split('T')[0],
      status: 'concluida', funcionario_solicitante_id: null, observacoes: '', escritorio_id: null,
    });
  };

  const openEdit = (m: ManutencaoVeiculo) => {
    setSelected(m);
    const valor = Number(m.valor) || 0;
    setValorFormatado(formatarMoedaParaInput(valor));
    setForm({
      veiculo_id: m.veiculo_id, tipo: m.tipo, problema: m.problema || '',
      descricao_servico: m.descricao_servico || '', mecanica: m.mecanica || '',
      valor: Number(m.valor) || 0, kilometragem: m.kilometragem || 0,
      data_manutencao: m.data_manutencao, status: m.status,
      funcionario_solicitante_id: m.funcionario_solicitante_id || null,
      observacoes: m.observacoes || '', escritorio_id: m.escritorio_id,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    if (!form.veiculo_id || !form.data_manutencao) {
      toast.error('Preencha veículo e data');
      return;
    }
    try {
      const payload = {
        veiculo_id: form.veiculo_id,
        tipo: form.tipo,
        problema: form.problema || undefined,
        descricao_servico: form.descricao_servico || undefined,
        mecanica: form.mecanica || undefined,
        valor: form.valor,
        kilometragem: form.kilometragem,
        data_manutencao: form.data_manutencao,
        status: form.status,
        funcionario_solicitante_id: form.funcionario_solicitante_id ?? undefined,
        observacoes: form.observacoes || undefined,
        escritorio_id: form.escritorio_id ?? undefined,
      };
      if (selected) {
        await frotaService.atualizarManutencao(selected.id, payload);
        toast.success('Manutenção atualizada');
      } else {
        await frotaService.criarManutencao(payload);
        toast.success('Manutenção registrada');
      }
      setOpenForm(false);
      setSelected(null);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao salvar');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await frotaService.excluirManutencao(selected.id);
      toast.success('Excluída');
      setOpenDelete(false);
      load();
      loadStats();
    } catch {
      toast.error('Erro ao excluir');
    }
  };

  const statItems: FrotaStatItem[] = [
    { key: 'todos', label: 'Total', value: stats.total, icon: <Build />, filterValue: '' },
    { key: 'pendente', label: 'Pendentes', value: stats.pendente, color: STATUS_COLORS.pendente, icon: <HourglassEmpty />, filterValue: 'pendente' },
    { key: 'agendada', label: 'Agendadas', value: stats.agendada, color: STATUS_COLORS.agendada, icon: <Event />, filterValue: 'agendada' },
    { key: 'em_andamento', label: 'Em andamento', value: stats.em_andamento, color: STATUS_COLORS.em_andamento, icon: <Autorenew />, filterValue: 'em_andamento' },
    { key: 'concluida', label: 'Concluídas', value: stats.concluida, color: STATUS_COLORS.concluida, icon: <CheckCircle />, filterValue: 'concluida' },
  ];

  return (
    <>
      <FrotaPageLayout
        title="Manutenções de Veículos"
        subtitle="Registro de manutenções preventivas, corretivas e requisições"
        icon={<Build fontSize="large" />}
        iconColor="#FF9800"
        stats={statItems}
        activeFilter={status}
        onFilterChange={(v) => { setStatus(v); setPage(1); }}
        loading={loading}
        actions={
          <>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => { load(); loadStats(); }} sx={{ borderRadius: 2 }}>
              Atualizar
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => { resetForm(); setSelected(null); setOpenForm(true); }} sx={{ borderRadius: 2, boxShadow: 2 }}>
              Nova manutenção
            </Button>
          </>
        }
        filters={
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <SysDateRangePicker
                label="Data"
                value={dataRange}
                onChange={(range) => { setDataRange(range); setPage(1); }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <VeiculoSelect
                label="Veículo"
                value={filtroVeiculoId}
                onChange={(v) => { setFiltroVeiculoId(v); setPage(1); }}
                apenasAtivos={false}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filtroTipo}
                  label="Tipo"
                  onChange={(e) => { setFiltroTipo(e.target.value as ManutencaoVeiculo['tipo'] | ''); setPage(1); }}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="preventiva">Preventiva</MenuItem>
                  <MenuItem value="corretiva">Corretiva</MenuItem>
                  <MenuItem value="requisicao">Requisição (técnico)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ borderRadius: 2 }}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="agendada">Agendada</MenuItem>
                  <MenuItem value="em_andamento">Em andamento</MenuItem>
                  <MenuItem value="concluida">Concluída</MenuItem>
                  <MenuItem value="cancelada">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        }
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Veículo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Problema / Serviço</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Mecânica</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>{m.data_manutencao}</TableCell>
                  <TableCell>{m.veiculo?.placa}</TableCell>
                  <TableCell><Chip size="small" label={m.tipo} /></TableCell>
                  <TableCell>{m.problema || m.descricao_servico || '-'}</TableCell>
                  <TableCell>{m.mecanica || m.fornecedor?.nome || '-'}</TableCell>
                  <TableCell>{m.valor ? frotaService.formatCurrency(Number(m.valor)) : '-'}</TableCell>
                  <TableCell><Chip size="small" label={frotaService.statusManutencaoLabel(m.status)} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => openEdit(m)}><Edit /></IconButton></Tooltip>
                    <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => { setSelected(m); setOpenDelete(true); }}><Delete /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={8}><Alert severity="info">Nenhuma manutenção</Alert></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
          </Box>
        )}
      </FrotaPageLayout>

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selected ? 'Editar manutenção' : 'Nova manutenção'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><VeiculoSelect value={form.veiculo_id} onChange={(v) => setForm({ ...form, veiculo_id: v })} required /></Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select value={form.tipo} label="Tipo" onChange={(e) => setForm({ ...form, tipo: e.target.value as ManutencaoVeiculo['tipo'] })}>
                  <MenuItem value="preventiva">Preventiva</MenuItem>
                  <MenuItem value="corretiva">Corretiva</MenuItem>
                  <MenuItem value="requisicao">Requisição (técnico)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => setForm({ ...form, status: e.target.value as ManutencaoVeiculo['status'] })}>
                  <MenuItem value="pendente">Pendente</MenuItem>
                  <MenuItem value="agendada">Agendada</MenuItem>
                  <MenuItem value="em_andamento">Em andamento</MenuItem>
                  <MenuItem value="concluida">Concluída</MenuItem>
                  <MenuItem value="cancelada">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Data" InputLabelProps={{ shrink: true }} value={form.data_manutencao} onChange={(e) => setForm({ ...form, data_manutencao: e.target.value })} /></Grid>
            <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="KM" value={form.kilometragem} onChange={(e) => setForm({ ...form, kilometragem: Number(e.target.value) })} /></Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth type="text" label="Valor (R$)"
                value={valorFormatado}
                onChange={(e) => {
                  const fmt = formatarValorOnInput(e.target.value);
                  setValorFormatado(fmt);
                  setForm((prev) => ({ ...prev, valor: parsearValorFormatado(fmt) }));
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Mecânica / Oficina" value={form.mecanica} onChange={(e) => setForm({ ...form, mecanica: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><FuncionariosSelect label="Solicitante" value={form.funcionario_solicitante_id} onChange={(v) => setForm({ ...form, funcionario_solicitante_id: v })} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Problema relatado" value={form.problema} onChange={(e) => setForm({ ...form, problema: e.target.value })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="O que foi feito" value={form.descricao_servico} onChange={(e) => setForm({ ...form, descricao_servico: e.target.value })} /></Grid>
            <Grid item xs={12} sm={6}><EscritorioSelect value={form.escritorio_id} onChange={(v) => setForm({ ...form, escritorio_id: v as number })} /></Grid>
            <Grid item xs={12}><TextField fullWidth multiline rows={2} label="Observações" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Excluir manutenção?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManutencoesPage;
